import React from 'react';
import { Zap, Coffee, Moon, BedDouble, Timer, AlertTriangle, Clock } from 'lucide-react';

/* ========== Formatters ========== */
const fmtHMS = (s) => {
  if (!s || s <= 0) return '0:00:00';
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  return `${h}:${String(m).padStart(2, '0')}:${String(sec).padStart(2, '0')}`;
};

const fmtHM = (s) => {
  if (!s || s <= 0) return '0h 0m';
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  return `${h}h ${m}m`;
};

const fmtCap = (s) => {
  if (!s) return '0:00';
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  return `${h}:${String(m).padStart(2, '0')}`;
};

const fmtTime = (iso) => {
  if (!iso) return '';
  return new Date(iso).toLocaleTimeString('en-AU', { hour: '2-digit', minute: '2-digit', hour12: true });
};

const fmtDate = (iso) => {
  if (!iso) return '';
  return new Date(iso).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
};

/* ========== Sub-components ========== */
const Bar = ({ value, max, color = 'bg-blue-500', warnAt, critAt }) => {
  const pct = max > 0 ? Math.min(100, (value / max) * 100) : 0;
  let c = color;
  if (critAt && pct >= critAt) c = 'bg-red-500';
  else if (warnAt && pct >= warnAt) c = 'bg-amber-500';
  return (
    <div className="w-full h-1.5 bg-slate-700 rounded-full overflow-hidden mt-1">
      <div className={`h-full ${c} rounded-full transition-all`} style={{ width: `${pct}%` }} />
    </div>
  );
};

const Badge = ({ text, bg, fg }) => (
  <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold ${bg} ${fg}`}>{text}</span>
);

const REASON_LABELS = {
  CONTINUOUS_WORK: 'Continuous Work Limit',
  DAILY_WORK_CAP: '24h Rolling Cap',
  ONE_AM_RULE: '1AM Rule',
  PROSPECTIVE_CONTINUOUS_WORK: 'Next Work Session',
};

/* ========== Main Component ========== */
const DriverComplianceDetail = ({ state }) => {
  if (!state) return null;

  const isFRHV = state.regime === 'FRHV';
  const isBusiness = state.regime === 'BUSINESS';
  const isWorking = state.current_segment?.segment_type === 'WORK';
  const isResting = state.current_segment?.segment_type === 'REST';
  const caps = state.rolling_caps || {};
  const rest = state.rest_tracking || {};

  const statusCfg = {
    GREEN: { bg: 'bg-emerald-500/15', fg: 'text-emerald-400', label: 'Compliant' },
    YELLOW: { bg: 'bg-amber-500/15', fg: 'text-amber-400', label: 'Warning' },
    RED: { bg: 'bg-red-500/15', fg: 'text-red-400', label: 'Critical' },
    OK: { bg: 'bg-emerald-500/15', fg: 'text-emerald-400', label: 'OK' },
    WARNING: { bg: 'bg-amber-500/15', fg: 'text-amber-400', label: 'Warning' },
    LIMIT_REACHED: { bg: 'bg-red-500/15', fg: 'text-red-400', label: 'Limit Reached' },
  }[state.compliance_status] || { bg: 'bg-slate-600/15', fg: 'text-slate-400', label: state.compliance_status };

  const regimeBadge = isFRHV
    ? { text: 'FRHV (Law)', bg: 'bg-blue-500/15', fg: 'text-blue-400' }
    : { text: 'Business Rules', bg: 'bg-cyan-500/15', fg: 'text-cyan-400' };

  const enforcementLabel = state.primary_layer === 'LAW' ? 'Full Enforce'
    : state.primary_layer === 'SHADOW_LAW' ? 'Shadow Law'
    : state.primary_layer || (isBusiness ? 'Business' : null);

  return (
    <div className="space-y-3 w-full" data-testid="driver-compliance-detail">

      {/* ====== Header: State + Badges — full width ====== */}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2">
          {isWorking ? <Zap className="w-5 h-5 text-red-400" /> : <Coffee className="w-5 h-5 text-emerald-400" />}
          <span className={`text-sm font-bold ${isWorking ? 'text-red-400' : 'text-emerald-400'}`}>
            {isWorking ? 'Working' : state.is_night_rest ? 'Night Rest' : state.is_day_off ? 'Day Off' : 'Resting'}
          </span>
          {state.current_segment?.elapsed_seconds > 0 && (
            <span className="text-xs text-slate-400 font-mono">for {fmtHM(state.current_segment.elapsed_seconds)}</span>
          )}
        </div>
        <div className="flex items-center gap-1.5 flex-wrap">
          <Badge text={statusCfg.label} bg={statusCfg.bg} fg={statusCfg.fg} />
          <Badge text={regimeBadge.text} bg={regimeBadge.bg} fg={regimeBadge.fg} />
          {enforcementLabel && <Badge text={enforcementLabel} bg="bg-slate-600/30" fg="text-slate-300" />}
          {state.provisional_mode && state.provisional_mode !== 'FULL' && (
            <Badge text={`Provisional (${state.actual_days || '?'}d)`} bg="bg-violet-500/15" fg="text-violet-400" />
          )}
          {state.vehicle && (
            <Badge
              text={`${state.vehicle.rego}${state.vehicle.gvm_kg ? ` • ${(state.vehicle.gvm_kg / 1000).toFixed(1)}t` : ''}`}
              bg="bg-slate-700/40" fg="text-slate-300"
            />
          )}
        </div>
      </div>

      {/* ====== REST BY / START WORK — full width banner ====== */}
      {isWorking && state.need_rest_by_ts && (
        <div className="bg-amber-500/10 border border-amber-500/20 rounded-xl px-4 py-2.5 flex items-center justify-between" data-testid="rest-by-panel">
          <div className="flex items-center gap-2">
            <Timer className="w-4 h-4 text-amber-400 flex-shrink-0" />
            <div>
              <span className="text-xs font-semibold text-amber-400">Rest By</span>
              <span className="text-[10px] text-amber-300/70 ml-2">{REASON_LABELS[state.need_rest_by_reason] || state.need_rest_by_reason}</span>
            </div>
          </div>
          <div className="text-right flex-shrink-0">
            <span className="text-sm font-bold text-amber-400 font-mono">
              {state.time_until_rest_required_sec != null && state.time_until_rest_required_sec > 0
                ? fmtHM(state.time_until_rest_required_sec) + ' remaining'
                : 'NOW'}
            </span>
            <span className="text-[10px] text-slate-400 ml-2">{fmtDate(state.need_rest_by_ts)}</span>
          </div>
        </div>
      )}

      {isResting && state.is_extended_rest && state.work_restart_remaining_sec != null && (
        <div className={`border rounded-xl px-4 py-2.5 flex items-center justify-between ${
          state.can_start_work ? 'bg-emerald-500/10 border-emerald-500/20' : 'bg-blue-500/10 border-blue-500/20'
        }`} data-testid="start-work-panel">
          <div className="flex items-center gap-2">
            <Zap className={`w-4 h-4 flex-shrink-0 ${state.can_start_work ? 'text-emerald-400' : 'text-blue-400'}`} />
            <div>
              <span className={`text-xs font-semibold ${state.can_start_work ? 'text-emerald-400' : 'text-blue-400'}`}>
                {state.can_start_work ? 'Ready to Work' : 'Start Work'}
              </span>
              <span className="text-[10px] text-slate-400 ml-2">
                {state.is_day_off ? '24h Day Off rest' : '7h Night Rest'}
              </span>
            </div>
          </div>
          <div className="text-right flex-shrink-0">
            <span className={`text-sm font-bold font-mono ${state.can_start_work ? 'text-emerald-400' : 'text-blue-400'}`}>
              {state.can_start_work ? 'REST COMPLETE' : fmtHM(state.work_restart_remaining_sec) + ' remaining'}
            </span>
            {state.work_restart_at && !state.can_start_work && (
              <span className="text-[10px] text-slate-400 ml-2">at {fmtTime(state.work_restart_at)}</span>
            )}
          </div>
        </div>
      )}

      {/* ====== Landscape grid — fills full width ====== */}
      <div className={`grid gap-2 ${isFRHV ? 'grid-cols-2 lg:grid-cols-4' : 'grid-cols-2 lg:grid-cols-3'}`}>

        {/* Continuous Work */}
        <div className="bg-slate-900/50 rounded-xl p-2.5 border border-slate-700/50">
          <div className="text-[10px] text-slate-500">Continuous Work</div>
          <div className="text-base font-bold text-white font-mono">{fmtHMS(state.continuous_work_seconds)}</div>
          {isFRHV && (
            <>
              <Bar value={state.continuous_work_seconds || 0} max={18900} color="bg-blue-500" warnAt={85} critAt={95} />
              <div className="text-[9px] text-slate-500 mt-0.5">Max 5:15</div>
            </>
          )}
          {isBusiness && state.work_limit_seconds && (
            <>
              <Bar value={state.continuous_work_seconds || 0} max={state.work_limit_seconds} color="bg-cyan-500" warnAt={80} critAt={95} />
              <div className="text-[9px] text-slate-500 mt-0.5">Limit {state.work_limit_display || fmtHM(state.work_limit_seconds)}</div>
            </>
          )}
        </div>

        {/* 24h Work */}
        <div className="bg-slate-900/50 rounded-xl p-2.5 border border-slate-700/50">
          <div className="text-[10px] text-slate-500">24h Work</div>
          <div className="text-base font-bold text-white font-mono">{fmtHMS(state.work_24h_seconds)}</div>
          {caps.roll_24h && (
            <>
              <Bar value={caps.roll_24h.actual} max={caps.roll_24h.law_limit} color="bg-purple-500" warnAt={85} critAt={95} />
              <div className="text-[9px] text-slate-500 mt-0.5">Max {fmtCap(caps.roll_24h.law_limit)}</div>
            </>
          )}
          {isBusiness && !caps.roll_24h && (
            <div className="text-[9px] text-slate-500 mt-1">Business regime</div>
          )}
        </div>

        {/* Rest Credits (FRHV) */}
        {isFRHV && state.R_credits_24 != null && (
          <div className="bg-slate-900/50 rounded-xl p-2.5 border border-slate-700/50" data-testid="rest-credits-panel">
            <div className="text-[10px] text-slate-500">Rest Credits (24h)</div>
            <div className="flex items-center justify-between mt-1">
              <div className="flex items-center gap-1">
                {Array.from({ length: state.max_credits_day || 4 }).map((_, i) => (
                  <div key={`credit-${i}`} className={`w-5 h-5 rounded-full border-2 flex items-center justify-center text-[9px] font-bold ${
                    i < (state.R_credits_24 || 0)
                      ? 'bg-emerald-500/30 border-emerald-500 text-emerald-400'
                      : 'bg-slate-700/50 border-slate-600 text-slate-600'
                  }`}>
                    {i < (state.R_credits_24 || 0) ? '✓' : ''}
                  </div>
                ))}
              </div>
              <div className="text-lg font-bold text-white font-mono">
                {state.R_credits_24 || 0}<span className="text-slate-500 text-sm">/{state.max_credits_day || 4}</span>
              </div>
            </div>
          </div>
        )}

        {/* 1AM Rule (FRHV) */}
        {isFRHV && state.one_am_constraint?.applies && (
          <div className="bg-indigo-500/10 rounded-xl p-2.5 border border-indigo-500/20" data-testid="one-am-panel">
            <div className="flex items-center gap-1.5">
              <Clock className="w-3.5 h-3.5 text-indigo-400" />
              <span className="text-[10px] font-semibold text-indigo-400">1AM Rule</span>
            </div>
            <div className="text-xs text-slate-300 mt-0.5">Night rest required</div>
            {state.one_am_constraint.deadline_utc && (
              <div className="text-xs font-mono text-indigo-400 mt-0.5">{fmtTime(state.one_am_constraint.deadline_utc)}</div>
            )}
          </div>
        )}

        {/* Business Rules Config */}
        {isBusiness && (
          <div className="bg-slate-900/50 rounded-xl p-2.5 border border-slate-700/50">
            <div className="text-[10px] text-slate-500">Business Config</div>
            <div className="mt-1 space-y-0.5">
              <div className="flex justify-between text-[11px]">
                <span className="text-slate-400">Work Limit</span>
                <span className="text-white font-mono">{state.work_limit_display || fmtHM(state.work_limit_seconds)}</span>
              </div>
              <div className="flex justify-between text-[11px]">
                <span className="text-slate-400">Rest Req</span>
                <span className="text-white font-mono">{state.rest_requirement_display || fmtHM(state.rest_requirement_seconds)}</span>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* ====== Rolling Windows (FRHV) — full width bar chart ====== */}
      {isFRHV && (caps.roll_7d || caps.roll_14d) && (
        <div className="bg-slate-900/50 rounded-xl p-3 border border-slate-700/50">
          <div className="text-[10px] text-slate-500 font-medium mb-2">Rolling Windows</div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {caps.roll_7d && (
              <div>
                <div className="flex items-center justify-between text-[11px] mb-0.5">
                  <span className="text-blue-400 font-medium">7-Day Work</span>
                  <span className="text-white font-mono">{fmtCap(caps.roll_7d.actual)} / {fmtCap(caps.roll_7d.law_limit)}</span>
                </div>
                <Bar value={caps.roll_7d.actual} max={caps.roll_7d.law_limit} color="bg-blue-500" warnAt={85} critAt={95} />
                {caps.roll_7d.shadow_exceeded && <span className="text-[9px] text-amber-400 font-medium">SHADOW EXCEEDED</span>}
                {caps.roll_7d.law_exceeded && <span className="text-[9px] text-red-400 font-medium">LAW EXCEEDED</span>}
              </div>
            )}
            {caps.roll_14d && (
              <div>
                <div className="flex items-center justify-between text-[11px] mb-0.5">
                  <span className="text-purple-400 font-medium">14-Day Work</span>
                  <span className="text-white font-mono">{fmtCap(caps.roll_14d.actual)} / {fmtCap(caps.roll_14d.law_limit)}</span>
                </div>
                <Bar value={caps.roll_14d.actual} max={caps.roll_14d.law_limit} color="bg-purple-500" warnAt={85} critAt={95} />
                {caps.roll_14d.shadow_exceeded && <span className="text-[9px] text-amber-400 font-medium">SHADOW EXCEEDED</span>}
                {caps.roll_14d.law_exceeded && <span className="text-[9px] text-red-400 font-medium">LAW EXCEEDED</span>}
              </div>
            )}
          </div>
        </div>
      )}

      {/* ====== Rest Requirements (FRHV) — landscape 2-col + counts ====== */}
      {isFRHV && (rest.night_rests_14d != null || rest.long_rests_14d != null) && (
        <div className="bg-slate-900/50 rounded-xl p-3 border border-slate-700/50" data-testid="rest-requirements-panel">
          <div className="text-[10px] text-slate-500 font-medium mb-2">REST REQUIREMENTS</div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {/* Night Rest */}
            <div>
              <div className="flex items-center gap-1.5 mb-0.5">
                <Moon className="w-3.5 h-3.5 text-amber-400" />
                <span className="text-[11px] text-white font-medium">Night Rest</span>
              </div>
              <div className="text-[9px] text-slate-500 mb-0.5">(7h in 24h)</div>
              <div className={`text-xs font-mono ${rest.night_rest_overdue ? 'text-red-400' : 'text-emerald-400'}`}>
                {rest.hours_since_night_rest != null
                  ? `${Math.floor(rest.hours_since_night_rest)}h ${Math.floor((rest.hours_since_night_rest % 1) * 60)}m ago`
                  : 'N/A'}
              </div>
              {rest.night_rest_overdue && (
                <div className="flex items-center gap-1 mt-0.5">
                  <AlertTriangle className="w-3 h-3 text-red-400" />
                  <span className="text-[9px] text-red-400 font-semibold">OVERDUE</span>
                </div>
              )}
              {rest.night_rest_required_by && !rest.night_rest_overdue && (
                <div className="text-[9px] text-slate-500 mt-0.5">Due {fmtDate(rest.night_rest_required_by)}</div>
              )}
            </div>

            {/* Long Rest */}
            <div>
              <div className="flex items-center gap-1.5 mb-0.5">
                <BedDouble className="w-3.5 h-3.5 text-violet-400" />
                <span className="text-[11px] text-white font-medium">Long Rest</span>
              </div>
              <div className="text-[9px] text-slate-500 mb-0.5">(24h in 7d)</div>
              <div className={`text-xs font-mono ${rest.long_rest_overdue ? 'text-red-400' : 'text-emerald-400'}`}>
                {rest.hours_since_long_rest != null
                  ? rest.hours_since_long_rest >= 24
                    ? `${Math.floor(rest.hours_since_long_rest / 24)}d ${Math.floor(rest.hours_since_long_rest % 24)}h ago`
                    : `${Math.floor(rest.hours_since_long_rest)}h ${Math.floor((rest.hours_since_long_rest % 1) * 60)}m ago`
                  : 'N/A'}
              </div>
              {rest.long_rest_overdue && (
                <div className="flex items-center gap-1 mt-0.5">
                  <AlertTriangle className="w-3 h-3 text-red-400" />
                  <span className="text-[9px] text-red-400 font-semibold">OVERDUE</span>
                </div>
              )}
              {rest.long_rest_required_by && !rest.long_rest_overdue && (
                <div className="text-[9px] text-slate-500 mt-0.5">Due {fmtDate(rest.long_rest_required_by)}</div>
              )}
            </div>

            {/* Night Rest Count */}
            <div className="text-center flex flex-col justify-center">
              <div className="text-2xl font-bold text-white">{rest.night_rests_14d ?? '-'}</div>
              <div className="text-[9px] text-slate-500">Night rests (14d)</div>
            </div>

            {/* Long Rest Count */}
            <div className="text-center flex flex-col justify-center">
              <div className="text-2xl font-bold text-white">{rest.long_rests_14d ?? '-'}</div>
              <div className="text-[9px] text-slate-500">Long rests (14d)</div>
            </div>
          </div>

          {/* Consecutive Night Rests + Long Night Work */}
          <div className="flex items-center gap-3 mt-2 pt-2 border-t border-slate-700/40">
            <div className="flex items-center gap-1.5" data-testid="consecutive-night-detail">
              <div className={`w-2 h-2 rounded-full ${rest.consecutive_night_rests_met ? 'bg-emerald-400' : 'bg-red-400'}`} />
              <span className={`text-[10px] font-medium ${rest.consecutive_night_rests_met ? 'text-emerald-400' : 'text-red-400'}`}>
                Consecutive night rests: {rest.consecutive_night_rests_met ? 'MET' : 'NOT MET'}
              </span>
            </div>
            {state.long_night_work?.active && (
              <div className="flex items-center gap-1.5 ml-auto" data-testid="long-night-work-detail">
                <AlertTriangle className="w-3 h-3 text-orange-400" />
                <span className="text-[10px] font-medium text-orange-400">
                  {state.long_night_work.reason === 'work_24h_exceeds_12h' ? 'Long work (>12h/24h)' : 'Night work (00-06)'}
                </span>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ====== Timezone ====== */}
      {state.server_time_base_tz && (
        <div className="text-[9px] text-slate-500">
          {state.server_time_base_tz} ({state.base_timezone?.split('/')[1]?.replace('_', ' ') || state.base_timezone})
        </div>
      )}
    </div>
  );
};

export default DriverComplianceDetail;
